import React, { useCallback, useMemo } from "react";

import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ResponseObject } from "../../classes/main";
import SearchServices from "../search-services/SearchServices";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import { TypeAnimation } from "react-type-animation";
import { getHubId } from "../genericComponents/helper-functions/currencyCode";

const SearchContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  margin-left: 45px;
`;

interface SearchBarProps {
  setReloadCartService: any;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  setReloadCartService,
}) => {
  const theme = useTheme();
  const isSmallScreenSearchBar = useMediaQuery(theme.breakpoints.down("sm"));

  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [optionList, setOptionList] = useState([]);
  const [searchServiceList, setSearchServiceList] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [serviceName, setServiceName] = useState("");
  const apiService = useMemo(() => new ApiService(), []);
  const xHubId = getHubId();
  const uniqueKeyOptions = optionList?.map((item, index) => ({
    id: index,
    label: item,
  }));

  const handleSelectedSearchService = (serviceName: any) => {
    setOpenSearchDialog(true);
    setServiceName(serviceName);
  };

  const getSearchServicesWithName = useCallback(async () => {
    try {
      if (serviceName) {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.searchServicesWithName + serviceName,
            undefined,
            xHubId
          );
        const response = responseObject?.object;
        setSearchServiceList(response);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [apiService, serviceName, xHubId]);

  useEffect(() => {
    getSearchServicesWithName();
  }, [serviceName, getSearchServicesWithName]);

  const getSearchServices = useCallback(async () => {
    try {
      if (inputValue) {
        const responseObject: ResponseObject | undefined =
          await apiService.get<ResponseObject>(
            apiService.searchServicesKeywords + inputValue
          );
        const response = responseObject?.object;
        setOptionList(response);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  }, [apiService, inputValue]);

  useEffect(() => {
    getSearchServices();
  }, [inputValue, getSearchServices]);

  return (
    <>
      <SearchContainer
        sx={{
          display: isSmallScreenSearchBar ? "none" : "initial", // Hide on small screens
        }}
      >
        <Box style={{ position: "relative" }}>
          <SearchIcon
            style={{
              position: "absolute",
              left: "-40px",
              marginTop: "16px",
              color: "#15741a",
            }}
          />
          <Autocomplete
            freeSolo={true}
            id="combo-box-demo-search-bar"
            options={uniqueKeyOptions}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              return option?.label ?? "";
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "clear") {
                setInputValue("");
                setOptionList([]);
              } else {
                setInputValue(newInputValue.toLowerCase());
              }
            }}
            sx={{
              width: "30vw",
              marginLeft: "-3rem",
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  border: "1px solid #15741a",
                },
              "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-colorPrimary.MuiInputBase-fullWidth.MuiInputBase-formControl.MuiAutocomplete-inputRoot":
                {
                  borderRadius: "100px",
                },
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option?.id}
                onClick={() => handleSelectedSearchService(option?.label)}
              >
                <Typography variant="body1">{option?.label}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                color="success"
                label={
                  <>
                    <span>Search for</span>&nbsp;
                    <TypeAnimation
                      sequence={[
                        "Body Polishing",
                        1000,
                        "Facial",
                        1000,
                        "Waxing",
                        1000,
                        "Manicure",
                        1000,
                        "Pedicure",
                        1000,
                      ]}
                      wrapper="span"
                      speed={1}
                      style={{
                        display: "inline-block",
                        fontWeight: "600",
                        color: "black",
                      }}
                      repeat={Infinity}
                    />
                  </>
                }
                InputLabelProps={{
                  sx: {
                    marginLeft: "20px",
                    paddingRight: "5px",
                    background: "white",
                    maxWidth: "calc(100% - 40px)",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    paddingLeft: "30px !important",
                    "& .MuiInputBase-input": {
                      paddingLeft: "30px",
                    },
                    borderRadius: "100px",
                    borderColor: "#15741a",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#15741a",
                    },
                  },
                }}
              />
            )}
            aria-label="Search services like Body Polishing, Facial, Waxing, Manicure, Pedicure" // Added for better accessibility
          />
        </Box>
      </SearchContainer>
      <SearchServices
        openSearchDialog={openSearchDialog}
        handleCloseSearchDialog={() => setOpenSearchDialog(false)}
        searchServiceList={searchServiceList}
        setSearchServiceList={setSearchServiceList}
        setServiceName={setServiceName}
        setReloadCartService={setReloadCartService}
      />
    </>
  );
};
